import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ImageOff, Layers, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { api, fileUrl } from "../lib/api";

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [intro, setIntro] = useState("");
  const [lightbox, setLightbox] = useState(null); // { photos, index, title, description }

  useEffect(() => {
    Promise.all([api.get("/gallery"), api.get("/gallery/folders")])
      .then(([g, f]) => { setItems(g.data); setFolders(f.data); })
      .finally(() => setLoading(false));
    api.get("/settings").then((r) => setIntro(r.data.texts?.gallery_intro || "")).catch(() => {});
  }, []);

  // Raggruppa le foto nella stessa cartella (folder_id) in un unico album col nome della cartella
  const folderMap = Object.fromEntries(folders.map((f) => [f.id, f.name]));
  const groups = [];
  const byFolder = {};
  for (const it of items) {
    const fid = it.folder_id || it.group_id;
    if (fid) {
      if (!byFolder[fid]) {
        byFolder[fid] = { key: fid, cover: it, photos: [], title: folderMap[fid] || it.title, description: it.description };
        groups.push(byFolder[fid]);
      }
      byFolder[fid].photos.push(it);
    } else {
      groups.push({ key: it.id, cover: it, photos: [it], title: it.title, description: it.description });
    }
  }

  const openLightbox = (g, index = 0) => setLightbox({ photos: g.photos, index, title: g.title, description: g.description });
  const close = () => setLightbox(null);
  const prev = useCallback(() => setLightbox((l) => (l ? { ...l, index: (l.index - 1 + l.photos.length) % l.photos.length } : l)), []);
  const next = useCallback(() => setLightbox((l) => (l ? { ...l, index: (l.index + 1) % l.photos.length } : l)), []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, prev, next]);

  const current = lightbox ? lightbox.photos[lightbox.index] : null;

  return (
    <div className="noise-overlay min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="mb-12">
          <span className="font-mono text-xs uppercase tracking-widest text-neon-pink">Portfolio</span>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white mt-2">
            La mia <span className="text-neon-cyan drop-shadow-[0_0_14px_rgba(0,240,255,0.5)]">galleria</span>
          </h1>
          <p className="text-muted-foreground font-light mt-3 max-w-lg">{intro || "Progetti stampati e incisi. Ogni pezzo racconta un'idea."}</p>
        </div>

        {loading ? (
          <p className="font-mono text-neon-cyan animate-pulse-glow">Caricamento…</p>
        ) : groups.length === 0 ? (
          <div data-testid="gallery-empty" className="flex flex-col items-center justify-center py-24 text-muted-foreground">
            <ImageOff className="w-12 h-12 mb-4 opacity-40" />
            <p className="font-light">Nessuna foto ancora. Torna presto!</p>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {groups.map((g, i) => {
              const isAlbum = g.photos.length > 1;
              return (
                <motion.button
                  key={g.key}
                  type="button"
                  onClick={() => openLightbox(g, 0)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: (i % 6) * 0.08 }}
                  data-testid={`gallery-item-${i}`}
                  className="group relative mb-5 w-full text-left break-inside-avoid overflow-hidden rounded-sm border border-white/10 bg-card hover:border-neon-cyan/60 transition-colors duration-300 cursor-pointer"
                >
                  <div className="overflow-hidden relative">
                    <img
                      src={fileUrl(g.cover.image_path)}
                      alt={g.title}
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <span className="print-scan" />
                    {isAlbum && (
                      <span data-testid={`gallery-album-badge-${i}`} className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-black/70 border border-white/15 text-white text-xs font-mono backdrop-blur-sm">
                        <Layers className="w-3.5 h-3.5 text-neon-cyan" /> {g.photos.length} foto
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-bold text-white text-lg tracking-tight">{g.title}</h3>
                    {g.photos.length === 1 && g.description && <p data-testid={`gallery-desc-${i}`} className="text-sm text-muted-foreground font-light mt-1.5 leading-relaxed">{g.description}</p>}
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && current && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
            className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-md flex flex-col"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-5 sm:px-8 py-4 border-b border-white/10" onClick={(e) => e.stopPropagation()}>
              <div className="min-w-0">
                <h3 className="font-display font-bold text-white text-lg tracking-tight truncate">{lightbox.title}</h3>
                {lightbox.photos.length > 1 && (
                  <p className="font-mono text-xs text-muted-foreground mt-0.5">{lightbox.index + 1} / {lightbox.photos.length}</p>
                )}
              </div>
              <button data-testid="lightbox-close" onClick={close} aria-label="Chiudi"
                className="shrink-0 w-10 h-10 flex items-center justify-center rounded-sm border border-white/15 text-white hover:border-neon-pink hover:text-neon-pink transition-colors duration-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Image */}
            <div className="flex-1 min-h-0 relative flex items-center justify-center px-4 sm:px-16 py-4">
              {lightbox.photos.length > 1 && (
                <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Precedente"
                  className="absolute left-2 sm:left-5 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-black/60 border border-white/15 text-white hover:bg-neon-cyan hover:text-black transition-colors duration-200">
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              <motion.img
                key={current.id}
                data-testid="lightbox-image"
                onClick={(e) => e.stopPropagation()}
                src={fileUrl(current.image_path)}
                alt={lightbox.title}
                initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}
                className="max-w-full max-h-full object-contain rounded-sm border border-white/10"
              />
              {lightbox.photos.length > 1 && (
                <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Successiva"
                  className="absolute right-2 sm:right-5 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-black/60 border border-white/15 text-white hover:bg-neon-cyan hover:text-black transition-colors duration-200">
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </div>

            {/* Thumbnails */}
            {lightbox.photos.length > 1 && (
              <div className="px-5 sm:px-8 py-4 border-t border-white/10 flex gap-2 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
                {lightbox.photos.map((p, k) => (
                  <button
                    key={p.id}
                    data-testid={`lightbox-thumb-${k}`}
                    onClick={() => setLightbox((l) => ({ ...l, index: k }))}
                    className={`shrink-0 w-16 h-16 rounded-sm overflow-hidden border-2 transition-colors duration-200 ${k === lightbox.index ? "border-neon-cyan" : "border-white/10 hover:border-white/40"}`}
                  >
                    <img src={fileUrl(p.image_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
